import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import AppLayout from '@/components/AppLayout';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import SetPasswordPage from '@/pages/SetPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import SOPListPage from '@/pages/SOPListPage';
import SOPDetailPage from '@/pages/SOPDetailPage';
import TasksPage from '@/pages/TasksPage';
import ProjectsPage from '@/pages/ProjectsPage';
import ChangeRequestsPage from '@/pages/ChangeRequestsPage';
import RateCardsPage from '@/pages/RateCardsPage';
import ContentHubPage from '@/pages/ContentHubPage';
import LeadsPage from '@/pages/LeadsPage';
import ReportsPage from '@/pages/ReportsPage';
import TeamPerformancePage from '@/pages/TeamPerformancePage';
import ClientReviewsPage from '@/pages/ClientReviewsPage';
import UsersPage from '@/pages/UsersPage';
import IntegrationsPage from '@/pages/IntegrationsPage';
import ActivityPage from '@/pages/ActivityPage';
import SubscriptionsPage from '@/pages/SubscriptionsPage';
import ClientsPage from '@/pages/ClientsPage';
import ClientProfilePage from '@/pages/ClientProfilePage';
import MessagesPage from '@/pages/MessagesPage';
import FormatApprovalsPage from '@/pages/FormatApprovalsPage';
import PeerKudosPage from '@/pages/PeerKudosPage';
import FinancePage from '@/pages/FinancePage';
import BooksPage from '@/pages/BooksPage';
import ProposalsPage from '@/pages/ProposalsPage';
import NewProposalPage from '@/pages/NewProposalPage';
import NewProductCatalogProposalPage from '@/pages/NewProductCatalogProposalPage';
import MessagingProposalWizard from '@/pages/MessagingProposalWizard';
import ProposalDetailPage from '@/pages/ProposalDetailPage';
import ProposalDuplicatesPage from '@/pages/ProposalDuplicatesPage';
import PriceChangeReportPage from '@/pages/PriceChangeReportPage';
import LeadsDryRunPage from '@/pages/LeadsDryRunPage';
import RateCatalogPage from '@/pages/RateCatalogPage';
import ClientPortalProposalsPage from '@/pages/ClientPortalProposalsPage';
import AssetsPage from '@/pages/AssetsPage';
import NewAssetPage from '@/pages/NewAssetPage';
import AssetDetailPage from '@/pages/AssetDetailPage';
import AssetScanPage from '@/pages/AssetScanPage';
import ExitClearancePage from '@/pages/ExitClearancePage';

const INTERNAL = ['Owner', 'Admin', 'Digital Marketer', 'Designer/Editor', 'Content Writer/Creator', 'Sales', 'Operations', 'Accounts', 'LED Van Operator', 'Intern'];

function ClientGuard({ allow, children }) {
  // 'allow' is 'internal' or 'all'
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (allow === 'internal' && user.role === 'Client') return <Navigate to="/" replace />;
  return children;
}

function RoleGuard({ roles, children }) {
  // Extra defense-in-depth for role-sensitive routes (e.g. Finance / Subscriptions).
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function PermGuard({ perm, children }) {
  // For routes gated by explicit permission (finance_reviewer, finance_admin, etc.).
  // Owner + Admin always pass via hasPerm().
  const { user, hasPerm } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!hasPerm(perm)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* QR scan entry point — accepts any user state, decides inside */}
            <Route path="/a/:id" element={<AssetScanPage />} />
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route index element={<DashboardPage />} />
              <Route path="sops" element={<ClientGuard allow="internal"><SOPListPage /></ClientGuard>} />
              <Route path="sops/:id" element={<ClientGuard allow="internal"><SOPDetailPage /></ClientGuard>} />
              <Route path="tasks" element={<ClientGuard allow="internal"><TasksPage /></ClientGuard>} />
              <Route path="projects" element={<ClientGuard allow="internal"><ProjectsPage /></ClientGuard>} />
              <Route path="change-requests" element={<ChangeRequestsPage />} />
              <Route path="rate-cards" element={<ClientGuard allow="internal"><RateCardsPage /></ClientGuard>} />
              <Route path="content" element={<ClientGuard allow="internal"><ContentHubPage /></ClientGuard>} />
              <Route path="leads" element={<ClientGuard allow="internal"><LeadsPage /></ClientGuard>} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="team-performance" element={<ClientGuard allow="internal"><TeamPerformancePage /></ClientGuard>} />
              <Route path="reviews" element={<ClientReviewsPage />} />
              <Route path="users" element={<ClientGuard allow="internal"><UsersPage /></ClientGuard>} />
              <Route path="integrations" element={<ClientGuard allow="internal"><IntegrationsPage /></ClientGuard>} />
              <Route path="subscriptions" element={<PermGuard perm="finance_reviewer"><SubscriptionsPage /></PermGuard>} />
              <Route path="clients" element={<ClientGuard allow="internal"><ClientsPage /></ClientGuard>} />
              <Route path="clients/:id" element={<ClientGuard allow="internal"><ClientProfilePage /></ClientGuard>} />
              <Route path="messages" element={<ClientGuard allow="internal"><MessagesPage /></ClientGuard>} />
              <Route path="activity" element={<ClientGuard allow="internal"><ActivityPage /></ClientGuard>} />
              <Route path="format-approvals" element={<ClientGuard allow="internal"><FormatApprovalsPage /></ClientGuard>} />
              <Route path="kudos" element={<ClientGuard allow="internal"><PeerKudosPage /></ClientGuard>} />
              <Route path="finance" element={<ClientGuard allow="internal"><FinancePage /></ClientGuard>} />
              <Route path="books" element={<PermGuard perm="finance_reviewer"><BooksPage /></PermGuard>} />
              <Route path="proposals" element={<PermGuard perm="proposals_view"><ProposalsPage /></PermGuard>} />
              <Route path="proposals/duplicates" element={<PermGuard perm="proposals_view"><ProposalDuplicatesPage /></PermGuard>} />
              <Route path="proposals/price-changes" element={<PermGuard perm="proposals_view"><PriceChangeReportPage /></PermGuard>} />
              <Route path="proposals/leads-dry-run" element={<PermGuard perm="proposals_view"><LeadsDryRunPage /></PermGuard>} />
              <Route path="proposals/new" element={<PermGuard perm="proposals_create"><NewProposalPage /></PermGuard>} />
              <Route path="proposals/:id/edit" element={<PermGuard perm="proposals_view"><NewProposalPage /></PermGuard>} />
              <Route path="proposals/new/catalog" element={<PermGuard perm="proposals_create"><NewProductCatalogProposalPage /></PermGuard>} />
              <Route path="proposals/new-messaging" element={<PermGuard perm="proposals_create"><MessagingProposalWizard /></PermGuard>} />
              <Route path="proposals/:id" element={<PermGuard perm="proposals_view"><ProposalDetailPage /></PermGuard>} />
              <Route path="portal/proposals" element={<ClientPortalProposalsPage />} />
              <Route path="rate-catalog" element={<PermGuard perm="rates_manage"><RateCatalogPage /></PermGuard>} />
              {/* Assets Module (Stage A) */}
              <Route path="assets" element={<ClientGuard allow="internal"><PermGuard perm="assets_view"><AssetsPage /></PermGuard></ClientGuard>} />
              <Route path="assets/new" element={<ClientGuard allow="internal"><PermGuard perm="assets_list"><NewAssetPage /></PermGuard></ClientGuard>} />
              <Route path="assets/clearance/:user_id" element={<ClientGuard allow="internal"><PermGuard perm="assets_view_all"><ExitClearancePage /></PermGuard></ClientGuard>} />
              <Route path="assets/:id" element={<ClientGuard allow="internal"><PermGuard perm="assets_view"><AssetDetailPage /></PermGuard></ClientGuard>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          <Toaster position="top-right" theme="dark" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
